import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiGet, apiSend } from '../../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmButton,
  DataTable,
  EmptyState,
  FieldRow,
  LoadingPage,
  Num,
  PageHeader,
  Stat,
  StatGrid,
  TextField,
  When,
  statusTone,
  type Column,
} from '../../ui';

interface InvitationRow {
  id: string;
  email: string;
  codeHint: string | null;
  note: string | null;
  expiresAt: string;
  redeemedAt: string | null;
  redeemedUserId: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/**
 * Invitations for the pilot cohort.
 *
 * THE CODE IS SHOWN EXACTLY ONCE, on the row that creates it. Only its SHA-256
 * is stored, so this page cannot show it again and neither can a leaked
 * database. The list keeps four characters of it, which is enough to answer
 * "my code starts with nxi-7K…" and useless to anybody else.
 *
 * The one-time panel is therefore the loudest thing on the screen, with the
 * consequence spelled out next to it — a code copied nowhere is a code that
 * has to be reissued.
 */
export function AdminInvitations(): React.JSX.Element {
  const [rows, setRows] = useState<InvitationRow[] | null>(null);
  const [requiresInvite, setRequiresInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ email: string; code: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const body = await apiGet<{ invitations: InvitationRow[]; requiresInvite: boolean }>(
        '/admin/invitations',
      );
      setRows(body.invitations);
      setRequiresInvite(body.requiresInvite);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load invitations.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const body = await apiSend<{ code: string; invitation: { email: string } }>(
        'POST',
        '/admin/invitations',
        { email, ...(note.trim() ? { note: note.trim() } : {}) },
      );
      setIssued({ email: body.invitation.email, code: body.code });
      setEmail('');
      setNote('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the invitation.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    setError(null);
    try {
      await apiSend('POST', `/admin/invitations/${id}/revoke`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke it.');
    }
  }

  if (rows === null && !error) return <LoadingPage eyebrow="Operator" title="Invitations" stats />;

  const list = rows ?? [];
  const open = list.filter((r) => stateOf(r) === 'OPEN').length;
  const used = list.filter((r) => stateOf(r) === 'USED').length;

  const columns: Array<Column<InvitationRow>> = [
    { key: 'email', header: 'Email', primary: true, cell: (row) => row.email },
    {
      key: 'code',
      header: 'Code',
      cell: (row) => <code className="chip quiet">…{row.codeHint ?? '????'}</code>,
    },
    {
      key: 'state',
      header: 'State',
      cell: (row) => {
        const state = stateOf(row);
        return <Badge tone={statusTone(state, 'invitation')}>{STATE_WORDS[state]}</Badge>;
      },
    },
    { key: 'expires', header: 'Expires', cell: (row) => <When value={row.expiresAt} /> },
    {
      key: 'note',
      header: 'Note',
      cell: (row) => row.note ?? <span className="muted">—</span>,
    },
    {
      key: 'action',
      header: 'Action',
      align: 'end',
      cell: (row) =>
        stateOf(row) === 'OPEN' ? (
          <ConfirmButton
            label="Revoke"
            small
            title={`Revoke the invitation for ${row.email}?`}
            question="The code stops working immediately. Anybody holding it needs a new one."
            confirmLabel="Revoke it"
            onConfirm={() => void revoke(row.id)}
          />
        ) : (
          <span className="muted">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Invitations"
        lead={
          requiresInvite
            ? 'Signup needs a code. Each one works only for the address it was issued to, and only once.'
            : 'Signup does not require a code right now — anybody can sign up without one. These are still honoured.'
        }
      />

      {!requiresInvite && (
        <Alert tone="waiting" title="Signup is open to anybody">
          <span>
            <code className="chip quiet">SIGNUP_REQUIRES_INVITE</code> is off, so these codes are a
            courtesy rather than a gate.
          </span>
        </Alert>
      )}

      {issued && (
        <Card variant="feature" title="Copy this code now" aside={<Badge tone="waiting">Shown once</Badge>}>
          <p>
            The invitation for <strong>{issued.email}</strong> is:
          </p>
          <p>
            <code className="chip">{issued.code}</code>
          </p>
          <Alert tone="waiting">
            Only its hash is stored. This is the only time it can be read — if you leave this page
            without copying it, the invitation has to be reissued. With no email provider
            configured, send it to them yourself.
          </Alert>
          <div className="actions">
            <Button
              variant="primary"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.code);
                setCopied(true);
              }}
            >
              {copied ? 'Copied' : 'Copy the code'}
            </Button>
            <Button onClick={() => setIssued(null)}>Done with it</Button>
          </div>
        </Card>
      )}

      <StatGrid>
        <Stat label="Invitations" value={<Num value={list.length} />} />
        <Stat label="Open" value={<Num value={open} />} note="Issued and not used yet." emphasis={open > 0} />
        <Stat label="Used" value={<Num value={used} />} note="Someone signed up with it." />
      </StatGrid>

      <Card title="Issue an invitation">
        <form onSubmit={create} className="form">
          <FieldRow>
            <TextField
              label="Email"
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hint="The code only works for this address."
            />
            <TextField
              label="Note"
              type="text"
              name="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              hint="Optional. Where they came from, so the list means something in a month."
            />
          </FieldRow>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create invitation'}
          </Button>
        </form>
      </Card>

      {error && <Alert tone="critical">{error}</Alert>}

      <Card title="Every invitation">
        <DataTable
          columns={columns}
          rows={list}
          getKey={(row) => row.id}
          empty={
            <EmptyState title="No invitations yet.">
              Issue one above. The code is readable exactly once, on the panel that creates it.
            </EmptyState>
          }
        />
      </Card>
    </>
  );
}

type InvitationState = 'REVOKED' | 'USED' | 'EXPIRED' | 'OPEN';

const STATE_WORDS: Record<InvitationState, string> = {
  REVOKED: 'Revoked',
  USED: 'Used',
  EXPIRED: 'Expired',
  OPEN: 'Open',
};

function stateOf(row: InvitationRow): InvitationState {
  if (row.revokedAt) return 'REVOKED';
  if (row.redeemedAt) return 'USED';
  /* Compared as instants, never as strings: two ISO strings in different
     offsets sort wrongly even though they name the same moment. */
  if (new Date(row.expiresAt).getTime() <= Date.now()) return 'EXPIRED';
  return 'OPEN';
}
